import { Component, OnInit } from '@angular/core';
import { AuthService, StripeService } from '../../../services/index';
import { Merchant } from '../../../models/index';
import { Constants } from 'src/app/app.constants';
import { HttpErrorResponse } from '@angular/common/http';
import { TwoOptionAlertComponent } from 'src/app/shared/components/two-option-alert/two-option-alert.component';
import { MatDialog } from '@angular/material/dialog';
import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { AnalyticsAbstract } from 'src/app/services/analytics/analytics.abstract';
import {
  BreakpointObserver,
  BreakpointState,
  Breakpoints,
} from '@angular/cdk/layout';
import { Observable, map } from 'rxjs';

@Component({
  selector: 'plans',
  templateUrl: 'plans.component.html',
  styleUrls: ['./plans.component.css'],
})
export class PlansComponent implements OnInit {
  title = '';
  subtitle: string = '';
  merchant: Merchant = {};
  plansLoading = false;
  subscriptionLoading = false;
  saving = false;
  error = false;

  options: { value: string; icon?: string; label: string }[] = [
    { value: 'gbp', label: 'GBP' },
    { value: 'usd', label: 'USD' },
    { value: 'eur', label: 'EUR' },
  ];

  typeOptions: { value: string; icon?: string; label: string }[] = [
    { value: 'year', label: 'Annual <b>(20% off)</b>' },
    { value: 'month', label: 'Monthly' },
  ];

  selectedLogo?: [{ name: string; content: ArrayBuffer }];

  plans: any[] = [];
  basis = 'year';
  currency = 'gbp';

  hasTrial = false;
  hasFree = false;

  renewalDate?: number;
  cancelDate?: number;
  billingPortalLoading = false;

  constructor(
    public dialog: MatDialog,
    public constants: Constants,
    private authService: AuthService,
    private stripeService: StripeService,
    private mixpanel: AnalyticsAbstract,
    private breakpointObserver: BreakpointObserver,
  ) {}

  ngOnInit() {
    this.getProducts();
    this.getSubscriptionInfo();
    this.subtitle = this.authService.getNestedUserProperty('merchant', 'name');

    this.hasTrial = this.authService.getUserProperty('hasTrial');
    this.hasFree = this.authService.getUserProperty('hasFree');
  }

  public isHandset$: Observable<boolean> = this.breakpointObserver
    .observe(Breakpoints.Handset)
    .pipe(
      map((result: BreakpointState) => {
        return result.matches;
      }),
    );

  get currentPlan(): string {
    if (this.hasFree) {
      return 'SPECIAL FREE';
    }

    if (this.hasTrial) {
      return 'TRIAL';
    } else {
      return this.constants.convertToTitleCase(
        this.authService.getUserProperty('plan'),
      );
    }
  }

  get trialEnds(): number {
    return this.constants.calculateDiff(
      this.authService.getUserProperty('trialEnds'),
    );
  }

  checkout(event: any): void {
    this.mixpanel.track(Constants.analytics_keys.selectPlan, {
      Plan: event?.nickname,
      Currency: this.currency,
      Basis: this.basis,
    });

    const isSelectingStarter = event?.lookup_key
      ?.toLowerCase()
      .includes('starter');

    if (isSelectingStarter) {
      this.showStarterConfirmation(event);
    } else {
      this.showStandardConfirmation(event);
    }
  }

  private showStandardConfirmation(event: any): void {
    const dialogRef = this.dialog.open(TwoOptionAlertComponent, {
      data: {
        title: 'Checkout',
        body: `The plan you have selected is <b>${event?.nickname}</b>, do you wish to continue?`,
        buttonOne: 'Cancel',
        buttonTwo: 'Yes',
      },
      scrollStrategy: new NoopScrollStrategy(),
      autoFocus: false,
      width: '350px',
      disableClose: true,
      panelClass: 'custom-dialog',
    });
    dialogRef.afterClosed().subscribe((option: number) => {
      if (option == 1) {
        this.proceedWithCheckout(event);
      }
    });
  }

  private showStarterConfirmation(event: any): void {
    const merchantObj = this.authService.merchantObj;
    const hasMicrositeEnabled = merchantObj?.microsite?.enabled ?? false;

    const growthPlan = this.plans.find((plan) =>
      plan.name?.toLowerCase().includes('growth'),
    );
    const growthPrice = growthPlan?.prices.find(
      (price: any) => price.recurring?.interval === this.basis,
    );

    let confirmationBody = `
      <p>You're about to subscribe to the <b>Starter</b> plan.</p>
      <br>
      <p><b>You'll be missing out on these Growth benefits:</b></p>
      <ul style="text-align: left; margin-top: 10px;">
        <li>Unlimited loyalty cards (Starter: 2 cards)</li>
        <li>Unlimited team members (Starter: 2 team members)</li>
        <li>Unlimited push notifications (Starter: 1 per month)</li>
        <li>AI message generation</li>
        <li>Scheduled push notifications</li>
      </ul>
    `;

    if (hasMicrositeEnabled) {
      confirmationBody += `
        <br>
        <p style="color: var(--app-timber); font-weight: 600;">
          ⚠️ You will also lose access to your microsite as this is only available on the Growth plan.
        </p>
      `;
    }

    confirmationBody += `
      <br>
      <p>Are you sure you want to continue with Starter?</p>
    `;

    const dialogRef = this.dialog.open(TwoOptionAlertComponent, {
      data: {
        title: 'Confirm Starter Plan',
        body: confirmationBody,
        buttonOne: 'Cancel',
        buttonTwo: 'Continue with Starter',
        buttonThree: growthPrice ? 'Switch to Growth' : undefined,
      },
      scrollStrategy: new NoopScrollStrategy(),
      autoFocus: false,
      width: '500px',
      disableClose: true,
      panelClass: 'custom-dialog',
    });

    dialogRef.afterClosed().subscribe((option: number) => {
      if (option == 1) {
        this.proceedWithCheckout(event);
      } else if (option == 2 && growthPrice) {
        this.proceedWithCheckout(growthPrice);
      }
    });
  }

  private proceedWithCheckout(event: any): void {
    this.mixpanel.track(Constants.analytics_keys.planSelected, {
      Plan: event?.nickname,
      Currency: this.currency,
      Basis: this.basis,
    });

    this.saving = true;
    this.stripeService.createCheckoutSession(event.lookup_key).subscribe({
      next: (res: any) => {
        if (res && res.url) {
          window.open(res.url, '_self');
        } else {
          this.handleSubscriptionUpdateSuccess(event?.nickname);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.handleSubscriptionUpdateError(error);
      },
    });
  }

  private handleSubscriptionUpdateSuccess(plan: string): void {
    this.mixpanel.track(Constants.analytics_keys.planUpdated, {
      Plan: plan,
      Currency: this.currency,
      Basis: this.basis,
    });

    const dialogRef = this.dialog.open(TwoOptionAlertComponent, {
      data: {
        title: 'Plan Updated',
        body: `Your subscription has been updated to ${plan}`,
        buttonTwo: 'OK',
      },
      scrollStrategy: new NoopScrollStrategy(),
      autoFocus: false,
      width: '350px',
      disableClose: true,
      panelClass: 'custom-dialog',
    });
    dialogRef.afterClosed().subscribe((option: number) => {
      window.location.reload();
    });
  }

  private handleSubscriptionUpdateError(error: HttpErrorResponse): void {
    this.saving = false;
    this.constants.snack(error.error.message);
  }

  getSubscriptionInfo(): void {
    this.subscriptionLoading = true;

    this.stripeService
      .getSubscriptionInfo()
      .subscribe({
        next: (res: any) => {
          if (res) {
            if (res.current_period_end) {
              this.renewalDate = res.current_period_end;
            }
            if (res.cancel_at) {
              this.cancelDate = res.cancel_at;
            }

            if (res.nickname?.toLowerCase().includes('month')) {
              this.basis = 'month';
            } else {
              this.basis = 'year';
            }

            const currencyOption = this.options.find(
              (el) => el.value == res.currency,
            );
            if (currencyOption) {
              this.currency = currencyOption.value;
            }
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.subscriptionLoading = false));
  }

  getProducts(): void {
    this.plansLoading = true;

    this.stripeService
      .getProducts()
      .subscribe({
        next: (res: any) => {
          if (res) {
            const { products, prices } = res;

            products.forEach((product: any) => {
              const pricesToAdd = prices.filter(
                (price: any) =>
                  price.product === product.id &&
                  price?.metadata?.visible !== 'false',
              );
              product.prices = pricesToAdd;

              if (product.plan) {
                product.mostPopular = product.plan.mostPopular;
              }

              if (!product.metadata.location) {
                const locationProducts = products.filter(
                  (el: any) => el?.metadata?.location === 'true',
                );

                const micrositeProducts = products.filter(
                  (el: any) => el?.metadata?.microsite === 'true',
                );

                let locationPrices: any[] = [];
                let micrositePrices: any[] = [];

                locationProducts.forEach((prod: any) => {
                  locationPrices = locationPrices.concat(
                    prices.filter((price: any) => price.product === prod.id),
                  );
                });

                micrositeProducts.forEach((prod: any) => {
                  micrositePrices = micrositePrices.concat(
                    prices.filter((price: any) => price.product === prod.id),
                  );
                });

                const monthly = locationPrices.find(
                  (el: any) =>
                    el.recurring?.interval === 'month' &&
                    el.metadata?.plan === product.metadata?.plan,
                );

                const annually = locationPrices.find(
                  (el: any) =>
                    el.recurring?.interval === 'year' &&
                    el.metadata?.plan === product.metadata?.plan,
                );

                const monthlyMicrosite = micrositePrices.find(
                  (el: any) =>
                    el.recurring?.interval === 'month' &&
                    el.metadata?.plan === product.metadata?.plan,
                );

                const annuallyMicrosite = micrositePrices.find(
                  (el: any) =>
                    el.recurring?.interval === 'year' &&
                    el.metadata?.plan === product.metadata?.plan,
                );

                if (
                  monthly &&
                  annually &&
                  monthly.currency_options &&
                  annually.currency_options
                ) {
                  product.extraLocation = {
                    monthly: {
                      gbp:
                        monthly.currency_options?.gbp?.unit_amount / 100 || 0,
                      usd:
                        monthly.currency_options?.usd?.unit_amount / 100 || 0,
                      eur:
                        monthly.currency_options?.eur?.unit_amount / 100 || 0,
                    },
                    annually: {
                      gbp:
                        annually.currency_options?.gbp?.unit_amount /
                          100 /
                          12 || 0,
                      usd:
                        annually.currency_options?.usd?.unit_amount /
                          100 /
                          12 || 0,
                      eur:
                        annually.currency_options?.eur?.unit_amount /
                          100 /
                          12 || 0,
                    },
                  };
                }

                if (
                  monthlyMicrosite &&
                  annuallyMicrosite &&
                  monthlyMicrosite.currency_options &&
                  annuallyMicrosite.currency_options
                ) {
                  product.extraMicrosite = {
                    monthly: {
                      gbp:
                        monthlyMicrosite.currency_options?.gbp?.unit_amount /
                          100 || 0,
                      usd:
                        monthlyMicrosite.currency_options?.usd?.unit_amount /
                          100 || 0,
                      eur:
                        monthlyMicrosite.currency_options?.eur?.unit_amount /
                          100 || 0,
                    },
                    annually: {
                      gbp:
                        annuallyMicrosite.currency_options?.gbp?.unit_amount /
                          100 /
                          12 || 0,
                      usd:
                        annuallyMicrosite.currency_options?.usd?.unit_amount /
                          100 /
                          12 || 0,
                      eur:
                        annuallyMicrosite.currency_options?.eur?.unit_amount /
                          100 /
                          12 || 0,
                    },
                  };
                }
              }
            });

            // Filter products for plans
            this.plans = products.filter(
              (el: any) =>
                !el?.metadata?.location &&
                !el?.metadata?.microsite &&
                el.active === true,
            );
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.plansLoading = false));
  }

  get loading(): boolean {
    return (
      this.plansLoading ||
      this.saving ||
      this.subscriptionLoading ||
      this.billingPortalLoading
    );
  }

  get hasActiveSubscription(): boolean {
    return !this.hasFree && !this.hasTrial;
  }

  get pageTitle(): string {
    return this.hasActiveSubscription ? 'Plans & Billing' : 'Choose a Plan';
  }

  openBillingPortal(): void {
    this.mixpanel.track(Constants.analytics_keys.openBillingPortal);

    this.billingPortalLoading = true;
    this.stripeService
      .getBillingPortalSession()
      .subscribe({
        next: (res: any) => {
          if (res?.url) {
            window.open(res.url, '_blank');
          }
        },
        error: (error: HttpErrorResponse) => {
          this.constants.snack(error.error.message);
        },
      })
      .add(() => (this.billingPortalLoading = false));
  }

  toggleCurrency(event: any): void {
    this.mixpanel.track(Constants.analytics_keys.togglePlanCurrency, {
      Currency: event,
    });

    this.currency = event;
  }

  toggleBasis(event: any): void {
    this.mixpanel.track(Constants.analytics_keys.togglePlanBasis, {
      Basis: event,
    });

    this.basis = event;
  }
}
